/**
 * SEO Data Utilities
 * Centralized SEO metadata for different page types
 */

// Explicit .js extensions so Node build scripts (scripts/prerender-meta.mjs)
// can import this module directly without a bundler.
import { getPublicBaseUrl } from "./publicBaseUrl.js";
import {
  BRAND_NAME,
  CONTACT_EMAIL,
  FOUNDER,
  PHONE_KUWAIT,
  PHONE_UAE,
  organizationSchema,
  personSchema,
} from "./contactInfo.js";
import {
  DEFAULT_SOCIAL_DESCRIPTION,
  DEFAULT_SOCIAL_TITLE,
} from "./socialMeta.js";
import { projectCategories } from "../pages/case-studies/projectData.js";
import { getProductDetailConfig } from "../data/productContent.js";
import { extractFaqPairs, faqEntitiesFromPairs, resolveBlogImage } from "./blogEnhance.js";
import {
  GULF_STATS_COVERAGE,
  GULF_STATS_CSV_PATH,
  GULF_STATS_LICENSE,
  GULF_STATS_PATH,
  GULF_STATS_UPDATED,
  SOURCES,
  gulfStatsFaqs,
} from "../data/gulfStats.js";

const BASE_URL = getPublicBaseUrl();
const orgEntity = () => organizationSchema(BASE_URL);
const personEntity = () => personSchema(BASE_URL);

/** Compact Person node for BlogPosting / Article author (same @id as site Person). */
const authorPersonRef = (baseUrl = BASE_URL) => ({
  "@type": "Person",
  "@id": `${baseUrl}/#person-danyal-jamil`,
  name: FOUNDER.name,
  url: `${baseUrl}${FOUNDER.urlPath}`,
  jobTitle: FOUNDER.jobTitle,
  sameAs: [FOUNDER.linkedIn],
});

/** Site content freshness for static pages (visible + schema when no CMS date). */
export const SITE_CONTENT_UPDATED = "2026-08-03";
export const SITE_CONTENT_UPDATED_LABEL = "August 2026";

const normalizeTitleWithBrand = (title = "") => {
  if (!title) return BRAND_NAME;
  const titleHasBrand =
    title.includes(BRAND_NAME) || title.includes(`${BRAND_NAME}.`) || title.includes("Jamil Global's");
  return titleHasBrand ? title : `${title} | ${BRAND_NAME}`;
};

export const faqEntitiesFromList = (faqs) =>
  faqs.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer,
    },
  }));

/** FAQ copy shared by on-page sections and FAQPage schema */
export const contactPageFaqs = [
  {
    question: "How quickly will you respond to my inquiry?",
    answer:
      `We aim to reply within one business day, often sooner. If your request is urgent, mention it in the message or call ${PHONE_KUWAIT.display} (Kuwait) or ${PHONE_UAE.display} (WhatsApp / UAE).`,
  },
  {
    question: "Is the first consultation free?",
    answer:
      "Yes — introductory strategy calls are typically complimentary so we can understand your goals and whether we are the right fit before you commit.",
  },
  {
    question: "What should I include in my message?",
    answer:
      "A short description of the problem, your timeline, approximate budget band, and any must-have integrations (CRM, WhatsApp, data sources, etc.) helps us respond with a concrete next step.",
  },
  {
    question: "Where is Jamil Global based?",
    answer:
      "Jamil Global is registered in the UAE and expanding to Kuwait. We deliver across Kuwait and the GCC with clear milestones, demos, and handover—mostly remote, with on-site when it matters.",
  },
];

export const whatWeDoPageFaqs = [
  {
    question: "What services does Jamil Global offer?",
    answer:
      "We lead with four offers: AI agents, workflow automation, AI integration, and AI consultancy that ships. Generative systems, data intelligence, and product engineering stay as supporting capabilities when an agent or automation needs them.",
  },
  {
    question: "Can you work with our existing stack?",
    answer:
      "Yes. We integrate with common cloud providers, CRMs, marketing tools, and in-house APIs. We prioritise maintainable patterns your team can operate after handover.",
  },
  {
    question: "Do you only work with large enterprises?",
    answer:
      "No — we work with growth-stage companies and established brands. Scope is shaped around a realistic roadmap, whether that is an MVP, an automation sprint, or a multi-team programme.",
  },
];

/** Home / landing — FAQPage JSON-LD on homepage (footer FAQ block removed) */
export const homePageFaqs = [
  {
    question: "I already use ChatGPT. How is this different?",
    answer:
      "ChatGPT answers when you type. We build helpers that run the job without you watching — reports, morning ad intel, paperwork — inside the tools you already use, with rules so they stay on-script.",
  },
  {
    question: "I don’t know what to automate. Can we still talk?",
    answer:
      "Yes. The free consult is thirty minutes. You describe a job that wastes time. We say whether we have shipped something like it, or we say no. You do not give us access to your business on that call.",
  },
  {
    question: "What happens on the free consultation?",
    answer:
      "Book a 30-minute call. We clarify the job, the tools involved, and constraints. If it fits, the next step is one scoped helper — not a slide deck. Urgent timing can be mentioned on the call or in the contact form.",
  },
  {
    question: "Do we have to change our tools?",
    answer:
      "No. We plug into what you already use — CRM, ads, Slack, docs, WhatsApp — so the helper shows up where your team already looks.",
  },
  {
    question: "Is Jamil Global an AI agency in Kuwait?",
    answer:
      "Yes. Jamil Global is registered in the UAE, expanding to Kuwait, and builds AI agents, automations, and integrations for Kuwaiti teams, with delivery across the GCC. Consultancy is the front door into shipping work, not a slide-only programme.",
  },
];

/** All /services/* pages (before Footer in ServiceLayout) */
export const servicePagesFaqs = [
  {
    question: "How long does a typical engagement take?",
    answer:
      "It depends on scope: focused automation or integration sprints can be weeks; platforms, agents, or multi-system programs often run across months. We quote milestones, not vague timelines.",
  },
  {
    question: "Can you work inside our security and vendor rules?",
    answer:
      "Yes. We routinely work with access controls, staging environments, and procurement-friendly documentation. Tell us your constraints early so architecture and contracts match reality.",
  },
  {
    question: "What happens after launch?",
    answer:
      "We agree a handover: documentation, monitoring hooks, and optional retainers for improvements. You should never be locked into mystery code nobody understands.",
  },
  {
    question: "Do you support Arabic or bilingual experiences?",
    answer:
      "Yes—many GCC-facing products need Arabic UI, RTL layouts, and localized content flows. We design for language and channel mix (web, WhatsApp, email) from the start when you need it.",
  },
];

/** /services/ai-consultancy */
export const aiConsultancyFaqs = [
  {
    question: "Do you provide AI consultancy in Kuwait?",
    answer:
      "Yes. Jamil Global provides AI consultancy in Kuwait from Kuwait City—opportunity assessment, strategy, architecture, vendor selection, and pilot design—with optional implementation of agents, automation, and integrations.",
  },
  {
    question: "Is AI consulting different from building the product?",
    answer:
      "Consultancy focuses on what to build, in what order, and under which constraints. Many clients then ask us to implement the first sprint so the roadmap becomes a shipped system rather than a slide deck.",
  },
  {
    question: "Who is AI consultancy in Kuwait for?",
    answer:
      "Leadership and ops teams that need clarity before large AI spend: SMEs and enterprises in Kuwait evaluating use cases, vendors, data readiness, and a practical path to production across the GCC when needed.",
  },
  {
    question: "How do we start an AI consultancy engagement?",
    answer:
      "Book a free consultation. We clarify goals and constraints, then propose an assessment or roadmap sprint with clear milestones. Urgent requests can mention timing on the call or in the contact form.",
  },
];

/** /services/conversational-ai */
export const conversationalAiFaqs = [
  {
    question: "Do you provide conversational AI consultancy for Kuwait businesses?",
    answer:
      "Yes. Jamil Global scopes conversational AI for WhatsApp and website chat in Arabic and English: which questions the bot may answer, which system holds the facts, when it should book, and when a CRM or staff handoff is the right build. Implementation is optional, by the same team.",
  },
  {
    question: "Can a WhatsApp or website chat answer in Arabic and English?",
    answer:
      "Yes. We design for language and channel mix (web, WhatsApp, email) from the start when you need it. The scope agrees which language each channel opens in and which Arabic and English content a native reviewer checks before launch.",
  },
  {
    question: "Should the chat answer a question or book a meeting?",
    answer:
      "It depends on the job. A bot that answers should draw on approved facts and escalate what it cannot answer. A bot that books should qualify the basics and write the consult into the calendar you already keep. The scoping call decides which fits, and some sites need both.",
  },
  {
    question: "When does a WhatsApp chat need a CRM instead of a chatbot?",
    answer:
      "When the chat produces a lead that must be written to your CRM and a person follows up, especially on the hot ones. In that case the first build is usually the CRM record and the handoff rule. A longer bot script rarely fixes a missing follow-up process.",
  },
  {
    question: "What happens on the first call?",
    answer:
      "It is a free 30-minute call. Bring one real chat workflow, one constraint, and an honest next step. No system access is needed. We clarify goals and constraints, then propose an assessment or roadmap sprint with clear milestones.",
  },
  {
    question: "Do you build the bot after the consultancy?",
    answer:
      "Optionally. The same team can implement agents, automation, and integrations. A pilot is designed as 4–8 weeks with clear KPIs, agreed data access, and a go/no-go before production. You can also take the written scope and decide afterwards.",
  },
  {
    question: "Who is responsible when the bot gives a wrong answer?",
    answer:
      "The scope names an owner for each reply type before launch, along with the source the answer comes from and a way to read the conversation. Wrong answers get fixed at the source, so the same mistake is corrected once.",
  },
  {
    question: "Where is Jamil Global based?",
    answer:
      "Registered in the UAE, expanding to Kuwait. We deliver across Kuwait and the GCC—mostly remote, with on-site when it matters.",
  },
];

/** /services/wordpress-development */
export const wordpressDevelopmentFaqs = [
  {
    question: "Do you build WordPress sites for businesses in Kuwait?",
    answer:
      "Yes. Jamil Global builds WordPress sites for businesses in Kuwait and the GCC. We can help with new sites, custom themes, changes to an existing WordPress site, speed and technical SEO, and maintenance after launch. The scope for each site is agreed with you before any build starts.",
  },
  {
    question: "Should my business site be on WordPress or Next.js?",
    answer:
      "WordPress usually fits when your team edits content often, the features you need exist as maintained plugins, and the site is mainly pages, posts, and forms. A Next.js or headless build fits better when the site hits a performance ceiling or needs product features plugins do not cover. We build both, so the recommendation follows the site.",
  },
  {
    question: "What should I check if my WordPress site needs Arabic and English?",
    answer:
      "Ask how the theme handles right-to-left layout, whether each language gets its own URLs, and whether menus, footers, and forms are translated too. Then look at the Arabic pages on a phone before launch, because a theme built for English does not become a good Arabic site by switching a language setting.",
  },
  {
    question: "What should I ask if the WordPress site takes payments in Kuwait?",
    answer:
      "Name the payment gateway and the plugin in the scope, then ask for three tests before launch: a sandbox order, a failed or cancelled payment, and a small live order with a refund. Also ask whose account owns the merchant login, because the business should control its own payments.",
  },
  {
    question: "Do you maintain WordPress sites after launch?",
    answer:
      "Maintenance can be part of the scope: keeping WordPress core, the theme, and plugins updated, checking the site after updates, and fixing what breaks. What is covered is agreed with you before launch, so updates have a named owner from day one.",
  },
  {
    question: "Can you improve the speed or SEO of an existing WordPress site?",
    answer:
      "Yes, we can help. We look at page weight, images, plugin load, and on-page basics such as titles, headings, internal links, and structured data, then tell you which fixes are worth doing on WordPress and which point to a different build.",
  },
  {
    question: "How do I start a WordPress project with Jamil Global?",
    answer:
      "Book a consultation or send a brief through the contact form. Bring the current site if there is one, the pages and features you need, the languages, and whether the site takes payments. We use that to scope the work before quoting.",
  },
  {
    question: "Where is Jamil Global based?",
    answer:
      "Registered in the UAE, expanding to Kuwait. We work with businesses across Kuwait and the GCC.",
  },
];

/** /services/performance-marketing */
export const performanceMarketingFaqs = [
  {
    question: "What does Jamil Global do in performance marketing?",
    answer:
      "We run paid media on Meta and Google Ads, including pay-per-call campaigns. The same team sets up the tracking, builds ROI reporting that puts ad spend next to revenue, and builds AI tools for media buying. We have managed $15M+ in ad spend for 71 and counting clients.",
  },
  {
    question: "Which ad platforms do you run?",
    answer:
      "Meta Ads (Facebook and Instagram) and Google Ads. Pay-per-call campaigns run on both, with call tracking connected so calls can be tied back to the ads that paid for them.",
  },
  {
    question: "Do you run pay-per-call campaigns?",
    answer:
      "Yes. At LeadVIPS, Danyal Jamil leads a 12-person PPC team running pay-per-call, Google Ads, and Facebook Ads across 5 verticals.",
  },
  {
    question: "How do you report on results?",
    answer:
      "We put ad spend from the ad accounts next to revenue from your call tracker or sales data, so the report shows ROI rather than clicks alone. Our ROI reporting saves $4,000+ and 100+ hours a month. Easy Growth Ops, our Slack tool for ROI and Drops, took LeadVIPS daily reporting from 3 hours to zero.",
  },
  {
    question: "What AI tools have you built for media buying?",
    answer:
      "An autonomous Google Ads agent, a competitor ad-funnel monitoring agent, and an AI ad scriptwriter. AI-driven media buying lifted revenue 75%.",
  },
  {
    question: "Do you work with agencies as well as brands?",
    answer:
      "Yes. An agency can bring us in to run Meta or Google Ads accounts, to fix tracking and reporting, or to build AI tooling for its own buyers. A brand can hire us for the same work on its own accounts.",
  },
  {
    question: "How does an engagement start?",
    answer:
      "Book a free 30-minute call or email hello@jamilglobal.com. Tell us which platforms you run, how you track revenue, and what a good month looks like. We then review the accounts and tracking and agree the scope before any work starts.",
  },
  {
    question: "Where is Jamil Global based?",
    answer: "Registered in the UAE, expanding to Kuwait.",
  },
];

/** /tools/ad-budget-calculator (visible FAQ, FAQPage JSON-LD, and crawler HTML) */
export const adBudgetCalculatorFaqs = [
  {
    question: "What is cost per lead (CPL)?",
    answer:
      "Cost per lead is your ad spend divided by the number of leads that spend produced. A lead is someone who gives you their details or contacts you. CPL shows what each lead costs, but it does not show which leads become paying customers.",
  },
  {
    question: "How is customer acquisition cost (CAC) different from cost per lead?",
    answer:
      "CAC is ad spend divided by paying customers, not leads. If some leads never buy, CAC is higher than CPL. The calculator uses your lead-to-customer rate to estimate it.",
  },
  {
    question: "What does ROAS mean in this calculator?",
    answer:
      "Return on ad spend (ROAS) is revenue divided by ad spend. Revenue here is customers multiplied by your average deal value, so ROAS is only as accurate as the deal value you enter. It is revenue, not profit: margin, delivery costs, and refunds are not subtracted.",
  },
  {
    question: "What does reverse mode do?",
    answer:
      "Reverse mode works backwards. Enter a target number of leads to see the budget, clicks, and impressions that would produce them. Or enter a target cost per lead to see the highest cost per click or CPM you can pay at your click-to-lead rate.",
  },
  {
    question: "Which currencies does the calculator support?",
    answer:
      "KWD, AED, SAR, QAR, and USD. The currency selector only labels the amounts. It does not convert between currencies, so enter every amount in the same currency.",
  },
  {
    question: "Why does the calculator not show average CPC or conversion rates for Kuwait or the UAE?",
    answer:
      "Benchmarks vary widely by industry, offer, platform, and season, so a published average can be misleading for your account. Use numbers from your own ad accounts. The example inputs on the page only show how the formulas work.",
  },
  {
    question: "Does the calculator save the numbers I enter?",
    answer:
      "No. The math runs in your browser, and the calculator does not save the numbers you enter. Use the copy button to keep a plain-text summary.",
  },
];

/** /services/ai-call-center-kuwait */
export const aiCallCenterKuwaitFaqs = [
  {
    question: "Which calls should a voice agent resolve, and which go to a person?",
    answer:
      "Calls with a fixed answer the agent can check against a source you name, such as opening hours, a ticket number, or a callback request. Anything that needs judgement, an exception, or a decision on an account goes to a person. The scope writes both lists down before any build.",
  },
  {
    question: "Can one line take Arabic and English?",
    answer:
      "Yes, with the language rule agreed in the scope. Kuwait callers often switch between Gulf Arabic and English in one sentence, so the scope sets the opening language, how the agent switches, and who reviews the Arabic wording. The scope includes a test of code-switching on real calls before launch.",
  },
  {
    question: "How fast must the reply feel?",
    answer:
      "The line owner sets the reply budget, because the right figure depends on the line and the caller. We do not quote a fixed figure. The reply time is measured on real calls before launch, and the scope records where any delay comes from.",
  },
  {
    question: "What must the agent refuse?",
    answer:
      "The scope lists what the agent may say, what it may only route to a person, and what it must refuse outright. Typical refusals include account decisions, refunds or exceptions, clinical or legal questions, and anything the agreed source does not cover.",
  },
  {
    question: "Can you clone a voice for the agent?",
    answer:
      "Only with a consent trail. That means written consent from the person whose voice is cloned, a record of permitted uses, and a usage policy with a named owner. Without that trail there is no clone, and the agent uses a voice from the models we work with.",
  },
  {
    question: "What does a person receive on handoff?",
    answer:
      "The context a person receives is agreed in the scope. It depends on the phone system and the record system the line already uses, and both need confirming before we describe the handoff in detail. Until then we do not promise what a handoff writes into any record system.",
  },
  {
    question: "Do you have a call-centre case study?",
    answer:
      "Not yet. We have not published a call-centre case study, so we make no claim about results on a live call line. Our general work is in the case studies section, and none of it is presented here as call-centre proof.",
  },
  {
    question: "What do we bring to the first call?",
    answer:
      "A recording of three real calls and the script you already use. The first call is free and lasts 30 minutes. We clarify goals and constraints, then propose an assessment or roadmap sprint with clear milestones.",
  },
  {
    question: "Where is Jamil Global based?",
    answer: "Registered in the UAE, expanding to Kuwait.",
  },
];

/** Optional per-slug FAQ overrides for /services/* JSON-LD + page FAQ */
export const serviceSlugFaqs = {
  "ai-consultancy": aiConsultancyFaqs,
  "conversational-ai": conversationalAiFaqs,
  "wordpress-development": wordpressDevelopmentFaqs,
  "performance-marketing": performanceMarketingFaqs,
  "ai-call-center-kuwait": aiCallCenterKuwaitFaqs,
};

/** /about */
export const aboutPageFaqs = [
  {
    question: "What is Jamil AI?",
    answer:
      "Jamil Global is an AI studio registered in the UAE and expanding to Kuwait that builds AI agents, workflow automations, and AI integrations. If you searched for “Jamil AI”, you most likely mean Jamil Global and its AI services; there is no separate product called Jamil AI. We have managed $15M+ in ad spend and worked with 71 and counting clients.",
  },
  {
    question: "Who is Jamil Global for?",
    answer:
      "Teams in Kuwait and the wider GCC that want pragmatic AI and software: marketing, operations, product, and leadership groups that care about measurable outcomes and maintainable systems.",
  },
  {
    question: "What makes your approach different?",
    answer:
      "We bias to shipping: clear scopes, visible milestones, and builds your team can run. We are as excited about reliable pipelines and UX as we are about models and agents.",
  },
  {
    question: "How big is your team?",
    answer:
      "Jamil Global was founded in October 2023. It is registered in the UAE and expanding to Kuwait. There is one core builder; we onboard employees and specialists per project based on need, so you get senior delivery without a bloated default headcount.",
  },
  {
    question: "What outcomes can you point to?",
    answer:
      "Over three years we managed ad spend, account creation, automations, video editing, and script writing—returning about 18–26% ROI on more than $15 million in spend. Individual case studies add project-level figures—time saved, accuracy, throughput, and scale—on the Work pages.",
  },
];

/** /ai-in-kuwait */
export const aiInKuwaitPageFaqs = [
  {
    question: "Is AI practical for Kuwaiti SMEs, not only enterprises?",
    answer:
      "Yes. The best ROI often comes from narrow, high-friction workflows—support, lead handling, reporting, or data prep—automated with guardrails and clear ownership.",
  },
  {
    question: "Do you offer AI consultancy in Kuwait as well as builds?",
    answer:
      "Yes. We provide AI consultancy in Kuwait—roadmaps, use-case prioritisation, and architecture—then implement agents, automation, and integrations when you want the same team to ship.",
  },
  {
    question: "What about data privacy and residency concerns?",
    answer:
      "We design around your policies: on-prem or regional cloud where required, least-privilege access, and audit-friendly logging. We map this in discovery before architecture is locked.",
  },
  {
    question: "Can AI work with our existing tools (CRM, ads, WhatsApp)?",
    answer:
      "That is the default path. We integrate with common stacks and APIs so AI sits where your team already works instead of forcing a parallel system.",
  },
];

/** /custom-solutions */
export const customSolutionsPageFaqs = [
  {
    question: "When is a custom build the right call?",
    answer:
      "When off-the-shelf tools cannot meet your workflow, data model, or compliance needs—or when glue code has become fragile. We validate that with you before committing to build.",
  },
  {
    question: "Can you maintain what you build?",
    answer:
      "Yes. We offer handover-first documentation plus optional ongoing support, monitoring, and iteration so systems stay healthy after launch.",
  },
  {
    question: "Do you sign NDAs and work under confidentiality?",
    answer:
      "Yes. Many builds involve sensitive data or strategy—we treat confidentiality as standard, not optional.",
  },
];

/** /products */
export const productsPageFaqs = [
  {
    question: "How are Jamil Global products different from your services?",
    answer:
      "Services are how we integrate, automate, and build bespoke software for your stack. Products—like Manzil, Ilmora, and Mehfil Media—are structured platforms we design and ship with repeatable modules, then tailor per client or market.",
  },
  {
    question: "Do we own the deployment or rent a shared tenant?",
    answer:
      "It depends on the product. Manzil and Ilmora are scoped as your operations or campus solution with custom deployment and handover. Mehfil Media runs as an AI UGC SaaS for Pakistani brands, with a live product surface at mehfil-media.jamilglobal.com.",
  },
  {
    question: "Can you support Arabic, English, Urdu, and regional deployment patterns?",
    answer:
      "Yes. We routinely implement bilingual experiences, RTL where needed, and market-native copy—especially for Kuwait ops products like Manzil, GCC education products, and Pakistan-facing UGC like Mehfil Media.",
  },
];

/** Per-slug FAQs for /products/:slug */
export const productSlugFaqs = {
  manzil: [
    {
      question: "What is Manzil?",
      answer:
        "Manzil is a Kuwait delivery operations platform: admin command center, merchant portal, driver app (web + native), and fleet-company portal on one realtime stack—orders, KWD wallets, live map dispatch, and bilingual EN/AR.",
    },
    {
      question: "Who is it for?",
      answer:
        "Restaurants, dark kitchens, platform ops teams, riders, and external delivery fleets that need a shared system of record instead of WhatsApp and spreadsheet handoffs.",
    },
    {
      question: "Where can we see the product?",
      answer:
        "Review the Manzil case study for live product screenshots across all four apps, or email manzil@jamilglobal.com / book a call to talk deployment for your fleet.",
    },
  ],
  ilmora: [
    {
      question: "Is Ilmora a boxed LMS or generic EdTEch app?",
      answer:
        "No. Ilmora is built and tailored by Jamil Global per institution—branding, roles, workflows, and integrations reflect your campus, not a one-size product catalog.",
    },
    {
      question: "What roles does Ilmora cover?",
      answer:
        "Typical deployments span administrators (oversight, users, subjects, sections), teachers (copilot, assessments, class workflows), and students (tutor, practice tests, assignments, results)—exact modules depend on your scope.",
    },
    {
      question: "Where do we start if we are interested?",
      answer:
        "Read the full product story on ilmora.jamilglobal.com, then book a call or email ilmora@jamilglobal.com—we’ll walk through white-label options, hosting and compliance, and a staged rollout plan.",
    },
  ],
  "mehfil-media": [
    {
      question: "What is Mehfil Media?",
      answer:
        "Mehfil Media is an AI UGC SaaS for Pakistani brands: upload a product photo, cast an AI creator from the Mehfil house, and generate Reel-style campaign videos with market-native hooks.",
    },
    {
      question: "How does the creation loop work?",
      answer:
        "Brands upload product images, choose an AI model/creator, and the system generates the UGC video—scripts, performance, and campaign cuts—typically framed as a ~48-hour photo-to-pack narrative.",
    },
    {
      question: "Where can we see it live?",
      answer:
        "Explore mehfil-media.jamilglobal.com for the brand story, work receipts, and category worlds—or email mehfil-media@jamilglobal.com / book a call to talk deployment and campaigns.",
    },
  ],
};

/** /dtaas */
export const dtaasPageFaqs = [
  {
    question: "What is Development as a Service (SDaaS) with Jamil Global?",
    answer:
      "Embedded capacity for discovery, build, and delivery—like adding a senior product-engineering lane without hiring a full department. You get predictable rhythm and clear ownership.",
  },
  {
    question: "How is pricing structured?",
    answer:
      "Typically milestone- or retainer-based against a roadmap. We align commercial structure to how you fund projects (capex vs opex) and how fast you need to move.",
  },
  {
    question: "Can SDaaS include AI and classic software together?",
    answer:
      "Yes. Most roadmaps mix APIs, interfaces, data pipelines, and AI features. We staff the slice you need each phase rather than forcing a single rigid team shape.",
  },
];

// Service-specific SEO data
export const serviceSEOData = {
  "ai-integration": {
    title: "Kuwait AI Integration & Enablement | Jamil Global",
    description:
      "AI integration and enablement for Kuwait: chatbots, custom models, NLP, computer vision, and analytics—scoped for Kuwaiti teams, delivered across the GCC.",
    keywords:
      "AI integration Kuwait, AI enablement Kuwait, AI projects Kuwait, AI agency Kuwait, chatbot Kuwait, machine learning Kuwait, NLP Kuwait, Kuwait AI, GCC AI, computer vision, predictive analytics",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "AI Integration",
      provider: {
        "@type": "Organization",
        name: "Jamil Global",
      },
      areaServed: [
        { "@type": "Country", name: "Saudi Arabia" },
        { "@type": "Country", name: "United Arab Emirates" },
        { "@type": "Country", name: "Qatar" },
        { "@type": "Country", name: "Kuwait" },
      ],
      description:
        "Expert AI integration services including custom model training, NLP, computer vision, and predictive analytics.",
    },
  },
  "ai-automation": {
    title: "Kuwait AI Automation & AI Empowerment | Jamil Global",
    description:
      "AI automation and empowerment for Kuwait: workflows, chatbots, and operations automation—from customer service to marketing and back-office—with rollout across the GCC when you need it.",
    keywords:
      "AI automation Kuwait, AI empowerment Kuwait, workflow automation Kuwait, AI chatbot Kuwait, business automation Kuwait, Kuwait AI, GCC AI, WhatsApp chatbot, AI assistant, Make.com, Zapier, n8n",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "AI Automation",
      provider: {
        "@type": "Organization",
        name: "Jamil Global",
      },
      areaServed: [
        { "@type": "Country", name: "Saudi Arabia" },
        { "@type": "Country", name: "United Arab Emirates" },
        { "@type": "Country", name: "Qatar" },
        { "@type": "Country", name: "Kuwait" },
      ],
    },
  },
  "ai-consultancy": {
    title: "AI Consultancy in Kuwait | Strategy & Roadmaps | Jamil Global",
    description:
      "AI consultancy in Kuwait from Kuwait City: opportunity assessment, AI strategy, architecture, vendor selection, and pilots that lead to production—not slide-only advice. GCC-ready delivery.",
    keywords:
      "AI consultancy in Kuwait, AI consultancy Kuwait, AI consulting Kuwait, AI consultant Kuwait, Kuwait AI consultancy, AI strategy Kuwait, AI advisory Kuwait, AI roadmap Kuwait, Kuwait City AI consulting, GCC AI consultancy",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "AI Consultancy in Kuwait",
      serviceType: "AI Consultancy",
      provider: {
        "@type": "Organization",
        name: "Jamil Global",
      },
      areaServed: [
        { "@type": "City", name: "Kuwait City" },
        { "@type": "Country", name: "Kuwait" },
        { "@type": "Country", name: "Saudi Arabia" },
        { "@type": "Country", name: "United Arab Emirates" },
        { "@type": "Country", name: "Qatar" },
      ],
      description:
        "AI consultancy in Kuwait: strategy, opportunity assessment, architecture, vendor selection, and pilot design with optional implementation.",
    },
  },
  "conversational-ai": {
    title: "Conversational AI Consultancy in Kuwait | Jamil Global",
    description:
      "Scope a WhatsApp or website chat bot in Arabic and English: which questions it answers, which system holds the facts, and when it should book.",
    keywords:
      "conversational AI consultants Kuwait, conversational AI consultant Kuwait, conversational AI consultancy Kuwait, conversational AI Kuwait, WhatsApp chatbot Kuwait, website chat bot Kuwait, Arabic English chatbot Kuwait, AI chatbot consultancy Kuwait, GCC conversational AI",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Conversational AI Consultancy in Kuwait",
      serviceType: "Conversational AI Consultancy",
      provider: {
        "@type": "Organization",
        name: "Jamil Global",
      },
      areaServed: [
        { "@type": "Country", name: "Kuwait" },
        { "@type": "Country", name: "Saudi Arabia" },
        { "@type": "Country", name: "United Arab Emirates" },
        { "@type": "Country", name: "Qatar" },
      ],
      description:
        "Conversational AI consultancy for WhatsApp and website chat in Arabic and English: allowed questions, systems of record, booking rules, and CRM handoff, with optional implementation.",
    },
  },
  "performance-marketing": {
    title: "Performance Marketing and Media Buying | Jamil Global",
    description:
      "Meta and Google Ads buying, pay-per-call, tracking, and ROI reporting, run by a team that also builds the AI tools. $15M+ in ad spend managed.",
    keywords:
      "performance marketing, media buying, paid media, meta ads, facebook ads, google ads, pay per call, ppc, ad tracking, roi reporting, ai media buying",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Performance Marketing and Media Buying",
      serviceType: "Performance Marketing",
      provider: {
        "@type": "Organization",
        name: "Jamil Global",
      },
      description:
        "Paid media on Meta and Google Ads, including pay-per-call campaigns, with tracking, ROI reporting, and AI tooling for media buying built by the same team.",
    },
  },
  "ai-call-center-kuwait": {
    title: "AI Call Center Solutions in Kuwait | Jamil Global",
    breadcrumbName: "AI Call Center Solutions in Kuwait",
    description:
      "Scope an AI voice agent for a Kuwait call line in Arabic and English: which calls it resolves, which it hands to a person, and what it must refuse.",
    keywords:
      "ai call center solutions kuwait, ai call center kuwait, call centre ai kuwait, arabic voice agent kuwait, ai voice agent kuwait, ai phone agent kuwait, ai receptionist kuwait, arabic english voice agent, inbound voice agent kuwait",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "AI Call Center Solutions in Kuwait",
      serviceType: "AI Voice Agent Consultancy",
      provider: {
        "@type": "Organization",
        name: "Jamil Global",
      },
      areaServed: "Kuwait and the GCC",
      description:
        "Scoping of inbound AI voice agents for phone lines in Arabic and English: which calls the agent resolves, which it hands to a person, what it must refuse, and how fast the reply must be, with optional build by the same team.",
    },
  },
  "wordpress-development": {
    title: "WordPress Developer in Kuwait | Jamil Global",
    description:
      "WordPress development for Kuwait businesses: new sites, custom themes, speed and SEO fixes, and maintenance, plus a straight WordPress vs Next.js call.",
    keywords:
      "wordpress developer kuwait, wordpress development kuwait, wordpress agency kuwait, wordpress website kuwait, custom wordpress theme kuwait, wordpress maintenance kuwait, مطور ووردبريس في الكويت",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "WordPress Development in Kuwait",
      serviceType: "WordPress Development",
      provider: {
        "@type": "Organization",
        name: "Jamil Global",
      },
      areaServed: [
        { "@type": "Country", name: "Kuwait" },
        { "@type": "Country", name: "Saudi Arabia" },
        { "@type": "Country", name: "United Arab Emirates" },
        { "@type": "Country", name: "Qatar" },
      ],
      description:
        "WordPress development for businesses in Kuwait and the GCC: new sites, custom themes, changes to existing sites, speed and technical SEO, and maintenance after launch.",
    },
  },
  "ai-agents": {
    title: "Kuwait AI Agents & Autonomous AI Projects | Jamil Global",
    description:
      "Build autonomous AI agents and intelligent assistants for Kuwaiti businesses—customer ops, content, and process agents—designed in Kuwait, extendable across the GCC.",
    keywords:
      "AI agents Kuwait, autonomous AI Kuwait, AI projects Kuwait, AI chatbot Kuwait, AI assistant Kuwait, Kuwait AI, GCC AI, CrewAI, OpenAI agents, workflow agents",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "AI Agents Development",
      provider: {
        "@type": "Organization",
        name: "Jamil Global",
      },
    },
  },
  "ai-video-generation": {
    title: "AI Video Generation — Kling, Seedance, Veo & More | Jamil Global",
    description:
      "AI video generation for Kuwait & GCC: Kling, Seedance, Veo 3.1, Runway, Hailuo, Wan, Luma, and more—multi-model SaaS, API integration, or done-for-you short-form engines.",
    keywords:
      "AI video generation Kuwait, Kling AI, Seedance, Google Veo 3, Runway Gen-4, Hailuo, Wan video, Luma Ray, HappyHorse, SkyReels, Vidu, PixVerse, AI video SaaS, text to video API, image to video Kuwait, GCC AI video",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "AI Video Generation",
      provider: {
        "@type": "Organization",
        name: "Jamil Global",
      },
      areaServed: [
        { "@type": "Country", name: "Saudi Arabia" },
        { "@type": "Country", name: "United Arab Emirates" },
        { "@type": "Country", name: "Qatar" },
        { "@type": "Country", name: "Kuwait" },
      ],
      description:
        "Multi-model AI video generation: SaaS products, API integration, and done-for-you pipelines across Kling, Seedance, Veo, Runway, and more.",
    },
  },
  "ai-image-generation": {
    title: "AI Image Generation — Flux, Midjourney, Nano Banana | Jamil Global",
    description:
      "AI image generation for Kuwait & GCC: GPT Image, Nano Banana, FLUX.2, Midjourney, Ideogram, Seedream, Recraft, Imagen—SaaS, APIs, or done-for-you creative production.",
    keywords:
      "AI image generation Kuwait, Flux AI, Midjourney, Nano Banana, GPT Image, Ideogram, Seedream, Recraft, Imagen 4, Stable Diffusion, AI image SaaS, text to image API, brand AI images Kuwait, GCC generative AI",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "AI Image Generation",
      provider: {
        "@type": "Organization",
        name: "Jamil Global",
      },
      areaServed: [
        { "@type": "Country", name: "Saudi Arabia" },
        { "@type": "Country", name: "United Arab Emirates" },
        { "@type": "Country", name: "Qatar" },
        { "@type": "Country", name: "Kuwait" },
      ],
      description:
        "Multi-model AI image generation: SaaS products, API integration, and brand-safe creative production across Flux, Midjourney, Nano Banana, Ideogram, and more.",
    },
  },
  "ai-text-generation": {
    title: "AI Text Generation — GPT, Claude, Gemini & More | Jamil Global",
    description:
      "AI text generation for Kuwait & GCC: GPT, Claude, Gemini, Grok, DeepSeek, Llama, Qwen, Mistral—multi-model SaaS, RAG copilots, or done-for-you content engines.",
    keywords:
      "AI text generation Kuwait, GPT-5, Claude AI, Gemini, Grok, DeepSeek, Llama, Qwen, Mistral, Kimi, AI writing SaaS, RAG Kuwait, content automation GCC, LLM API integration",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "AI Text Generation",
      provider: {
        "@type": "Organization",
        name: "Jamil Global",
      },
      areaServed: [
        { "@type": "Country", name: "Saudi Arabia" },
        { "@type": "Country", name: "United Arab Emirates" },
        { "@type": "Country", name: "Qatar" },
        { "@type": "Country", name: "Kuwait" },
      ],
      description:
        "Multi-model AI text generation: SaaS writing products, RAG copilots, and done-for-you content engines across GPT, Claude, Gemini, and open LLMs.",
    },
  },
  "ai-voice-generation": {
    title: "AI Voice Generation — ElevenLabs, Cartesia & More | Jamil Global",
    description:
      "AI voice generation for Kuwait & GCC: ElevenLabs, Cartesia Sonic, OpenAI TTS & Realtime, Gemini Live, Hume—voice SaaS, agents, cloning, or done-for-you narration.",
    keywords:
      "AI voice generation Kuwait, ElevenLabs, Cartesia Sonic, OpenAI TTS, Gemini Live, Hume AI, voice cloning Kuwait, text to speech API, AI voice agent GCC, TTS SaaS",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "AI Voice Generation",
      provider: {
        "@type": "Organization",
        name: "Jamil Global",
      },
      areaServed: [
        { "@type": "Country", name: "Saudi Arabia" },
        { "@type": "Country", name: "United Arab Emirates" },
        { "@type": "Country", name: "Qatar" },
        { "@type": "Country", name: "Kuwait" },
      ],
      description:
        "Multi-model AI voice generation: SaaS products, realtime agents, cloning, and done-for-you narration across ElevenLabs, Cartesia, OpenAI, Gemini, and more.",
    },
  },
  backend: {
    title: "Backend & APIs for Kuwait AI & Software Projects | Jamil Global",
    description:
      "Backend development, APIs, and data layers that power AI and product work in Kuwait—Python, Node.js, and cloud-ready architecture with GCC delivery.",
    keywords:
      "backend development Kuwait, API development Kuwait, Python Kuwait, Node.js Kuwait, Kuwait software, GCC, cloud architecture, database design, server development",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "Backend Development",
      provider: {
        "@type": "Organization",
        name: "Jamil Global",
      },
    },
  },
  frontend: {
    title: "Website & Frontend Development in Kuwait | Jamil Global",
    description:
      "Websites and frontends for Kuwaiti brands—WordPress, React, and modern stacks—e-commerce, marketing sites, and product UI, with GCC rollout as needed.",
    keywords:
      "website development Kuwait, web development Kuwait, WordPress Kuwait, React Kuwait, frontend Kuwait, Kuwait digital, GCC, UI UX, e-commerce Kuwait, responsive design",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "Frontend Development",
      provider: {
        "@type": "Organization",
        name: "Jamil Global",
      },
    },
  },
  "saas-development": {
    title: "SaaS & Product Development for Kuwait | Jamil Global",
    description:
      "Custom SaaS and cloud products built for Kuwaiti companies—discovery through launch—with scalable architecture and GCC-friendly operations.",
    keywords:
      "SaaS development Kuwait, software development Kuwait, cloud software Kuwait, custom software Kuwait, Kuwait startup, GCC, web application, product development",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "SaaS Development",
      provider: {
        "@type": "Organization",
        name: "Jamil Global",
      },
    },
  },
  "mobile-development": {
    title: "Mobile App Development in Kuwait | Jamil Global",
    description:
      "iOS and Android apps for Kuwait—native and cross-platform builds that pair with your AI and web stack, with GCC distribution when required.",
    keywords:
      "mobile app development Kuwait, iOS Kuwait, Android Kuwait, React Native Kuwait, Kuwait apps, GCC, app developer Kuwait, cross-platform apps",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "Mobile App Development",
      provider: {
        "@type": "Organization",
        name: "Jamil Global",
      },
    },
  },
  scraping: {
    title: "Web Scraping & Data for Kuwait AI Projects | Jamil Global",
    description:
      "Reliable scraping and data pipelines that feed Kuwait AI and analytics workloads—Python-based extraction, monitoring, and delivery across the GCC.",
    keywords:
      "web scraping Kuwait, data extraction Kuwait, data pipeline Kuwait, Python scraping, Kuwait AI data, GCC, data collection, crawling, mining",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "Web Scraping",
      provider: {
        "@type": "Organization",
        name: "Jamil Global",
      },
    },
  },
};

// Page-specific SEO data
export const pageSEOData = {
  home: {
    title: DEFAULT_SOCIAL_TITLE,
    description: DEFAULT_SOCIAL_DESCRIPTION,
    keywords:
      "AI agency Kuwait, Kuwait AI agency, AI agents Kuwait, AI automation Kuwait, AI consultancy in Kuwait, AI integration Kuwait, Kuwait AI studio, ops automation Kuwait, document AI Kuwait, GCC AI",
    structuredData: {
      "@context": "https://schema.org",
      "@graph": [
        orgEntity(),
        personEntity(),
        {
          "@type": "WebSite",
          "@id": `${BASE_URL}/#website`,
          url: BASE_URL,
          name: BRAND_NAME,
          alternateName: "Jamil Global Kuwait AI Agency",
          inLanguage: "en",
          description: DEFAULT_SOCIAL_DESCRIPTION,
          publisher: { "@id": `${BASE_URL}/#organization` },
          about: { "@id": `${BASE_URL}/#organization` },
        },
        {
          "@type": "WebPage",
          "@id": `${BASE_URL}/#webpage`,
          url: BASE_URL,
          name: DEFAULT_SOCIAL_TITLE,
          description: DEFAULT_SOCIAL_DESCRIPTION,
          isPartOf: { "@id": `${BASE_URL}/#website` },
          about: { "@id": `${BASE_URL}/#organization` },
          primaryImageOfPage: {
            "@type": "ImageObject",
            url: `${BASE_URL}/og-image.png`,
          },
        },
        {
          "@type": "FAQPage",
          "@id": `${BASE_URL}/#home-faq`,
          mainEntity: faqEntitiesFromList(homePageFaqs),
        },
      ],
    },
  },
  about: {
    title: "About Jamil Global | Kuwait AI Agency",
    description:
      "Jamil Global is registered in the UAE and expanding to Kuwait. Founded October 2023 by Danyal Jamil. One core builder, specialists per project. 18 to 26% ROI on $15M+ ad spend we managed end-to-end over three years.",
    keywords:
      "about Jamil Global, Danyal Jamil, UAE AI company, AI agency Kuwait, Kuwait AI agency, software development Kuwait, chatbot company Kuwait, GCC tech company",
    image: "/pictures/ai.webp",
    structuredData: {
      "@context": "https://schema.org",
      "@graph": [
        orgEntity(),
        personEntity(),
        {
          "@type": "AboutPage",
          "@id": `${BASE_URL}/about#webpage`,
          url: `${BASE_URL}/about`,
          name: "About Jamil Global | Kuwait AI Agency",
          description:
            "Jamil Global is registered in the UAE and expanding to Kuwait. Founded October 2023 by Danyal Jamil. One core builder, specialists per project. 18 to 26% ROI on $15M+ ad spend we managed end-to-end over three years.",
          isPartOf: { "@id": `${BASE_URL}/#website` },
          about: { "@id": `${BASE_URL}/#organization` },
          mainEntity: { "@id": `${BASE_URL}/#organization` },
        },
        {
          "@type": "FAQPage",
          "@id": `${BASE_URL}/about#faq`,
          mainEntity: faqEntitiesFromList(aboutPageFaqs),
        },
      ],
    },
  },
  "author-danyal-jamil": {
    title: "Danyal Jamil | Founder, Jamil Global",
    description:
      "Danyal Jamil is founder and principal builder at Jamil Global, registered in the UAE and expanding to Kuwait. He ships AI agents, automation, and product systems for Kuwait and GCC teams.",
    keywords:
      "Danyal Jamil, Jamil Global founder, AI agency Kuwait, Kuwait AI consultant, AI agents Kuwait",
    image: "/pictures/ai.webp",
    structuredData: {
      "@context": "https://schema.org",
      "@graph": [
        orgEntity(),
        personEntity(),
        {
          "@type": "ProfilePage",
          "@id": `${BASE_URL}${FOUNDER.urlPath}#webpage`,
          url: `${BASE_URL}${FOUNDER.urlPath}`,
          name: "Danyal Jamil | Founder, Jamil Global",
          description:
            "Danyal Jamil is founder and principal builder at Jamil Global, registered in the UAE and expanding to Kuwait. He ships AI agents, automation, and product systems for Kuwait and GCC teams.",
          isPartOf: { "@id": `${BASE_URL}/#website` },
          about: { "@id": `${BASE_URL}/#person-danyal-jamil` },
          mainEntity: { "@id": `${BASE_URL}/#person-danyal-jamil` },
          dateModified: SITE_CONTENT_UPDATED,
        },
      ],
    },
  },

  privacy: {
    title: "Privacy Policy | Jamil Global",
    description:
      "How Jamil Global handles contact-form data, Calendly bookings, and Vercel Analytics on jamilglobal.com. Plain-language privacy notice.",
    keywords: "Jamil Global privacy policy, privacy, data protection",
    image: "/og-image.png",
    structuredData: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": `${BASE_URL}/privacy#webpage`,
          url: `${BASE_URL}/privacy`,
          name: "Privacy Policy | Jamil Global",
          description:
            "How Jamil Global handles contact-form data, Calendly bookings, and Vercel Analytics on jamilglobal.com.",
          isPartOf: { "@id": `${BASE_URL}/#website` },
          about: { "@id": `${BASE_URL}/#organization` },
        },
      ],
    },
  },
  terms: {
    title: "Terms of Service | Jamil Global",
    description:
      "Terms for using jamilglobal.com and contacting Jamil Global. Project work is covered by a separate written agreement.",
    keywords: "Jamil Global terms of service, terms, legal",
    image: "/og-image.png",
    structuredData: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": `${BASE_URL}/terms#webpage`,
          url: `${BASE_URL}/terms`,
          name: "Terms of Service | Jamil Global",
          description:
            "Terms for using jamilglobal.com and contacting Jamil Global.",
          isPartOf: { "@id": `${BASE_URL}/#website` },
          about: { "@id": `${BASE_URL}/#organization` },
        },
      ],
    },
  },
  "what-we-do": {
    title: "AI Agents & Automation Kuwait | What We Do | Jamil Global",
    description:
      "AI studio registered in the UAE and expanding to Kuwait: agents, workflow automation, integrations, and consultancy that ships — plus generative systems, data intelligence, and product engineering when the workflow needs them.",
    keywords:
      "AI agents Kuwait, AI automation Kuwait, AI agency Kuwait, AI consultancy in Kuwait, AI integration Kuwait, workflow automation Kuwait, document AI Kuwait, GCC AI",
    image: "/pictures/ai.webp",
    structuredData: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": `${BASE_URL}/what-we-do`,
          url: `${BASE_URL}/what-we-do`,
          name: "AI Agents & Automation Kuwait | What We Do | Jamil Global",
          description:
            "Agents, automation, integrations, and consultancy that ships—Kuwait-first, GCC-capable.",
          isPartOf: {
            "@type": "WebSite",
            "@id": `${BASE_URL}/#website`,
            name: BRAND_NAME,
            url: BASE_URL,
          },
          about: { "@id": `${BASE_URL}/#organization` },
        },
        {
          "@type": "FAQPage",
          "@id": `${BASE_URL}/what-we-do#faq`,
          mainEntity: faqEntitiesFromList(whatWeDoPageFaqs),
        },
      ],
    },
  },
  "contact-us": {
    title: "Contact Jamil Global | Kuwait AI Agency",
    image: "/pictures/og/contact-us.png",
    description: `Contact Jamil Global (registered in the UAE, expanding to Kuwait)—free intro call for AI consultancy, agents, automation, and product builds. ${PHONE_KUWAIT.display} · ${CONTACT_EMAIL}`,
    keywords:
      "contact Jamil Global, Kuwait AI agency contact, AI consultancy in Kuwait, AI project Kuwait, free consultation Kuwait, AI enablement contact, GCC AI contact, hello@jamilglobal.com, Kuwait City",
    structuredData: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "ContactPage",
          "@id": `${BASE_URL}/contact-us#webpage`,
          name: "Contact Jamil Global | Kuwait AI Agency",
          url: `${BASE_URL}/contact-us`,
          description:
            "Contact Jamil Global for AI consultancy in Kuwait—agents, automation, integrations, and full builds—with a free consultation.",
          mainEntity: { "@id": `${BASE_URL}/#organization` },
          about: orgEntity(),
        },
        {
          "@type": "FAQPage",
          "@id": `${BASE_URL}/contact-us#faq`,
          mainEntity: faqEntitiesFromList(contactPageFaqs),
        },
      ],
    },
  },
  "custom-solutions": {
    title: "Custom AI Solutions Kuwait | Agency Projects | Jamil Global",
    description:
      "Custom AI automation, scraping, chatbots, and software from a Kuwait AI agency—scoped for local businesses and scaled across the GCC when you need it.",
    keywords:
      "custom AI Kuwait, AI automation Kuwait, AI projects Kuwait, chatbot solutions Kuwait, data scraping Kuwait, Kuwait AI agency, GCC, bespoke software Kuwait",
    image: "/pictures/ai.webp",
    structuredData: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "FAQPage",
          "@id": `${BASE_URL}/custom-solutions#faq`,
          mainEntity: faqEntitiesFromList(customSolutionsPageFaqs),
        },
      ],
    },
  },
  products: {
    title: "Jamil Global Products | Manzil, Ilmora, Mehfil Media",
    description:
      "Explore Jamil Global products—Manzil for Kuwait delivery ops, Ilmora for institutional AI learning, and Mehfil Media for AI UGC campaigns built for Pakistani brands.",
    keywords:
      "Manzil, Ilmora, Mehfil Media, Jamil Global products, Kuwait delivery platform, AI UGC Pakistan, school AI platform, education AI Kuwait, white label learning platform GCC",
    image: "/pictures/ai.webp",
    structuredData: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "FAQPage",
          "@id": `${BASE_URL}/products#faq`,
          mainEntity: faqEntitiesFromList(productsPageFaqs),
        },
      ],
    },
  },
  "ai-in-kuwait": {
    title: "AI Agency in Kuwait | Use Cases & Enablement | Jamil Global",
    description:
      "Practical AI agency work in Kuwait: use cases, AI consultancy, implementation paths, and AI enablement programs that ship into operations—Kuwait-first, GCC-ready.",
    keywords:
      "AI agency in Kuwait, AI in Kuwait, Kuwait AI agency, Kuwait AI empowerment, AI enablement Kuwait, AI use cases Kuwait, AI automation Kuwait, AI consulting Kuwait, AI consultancy in Kuwait, AI consultancy Kuwait",
    image: "/pictures/ai.webp",
    structuredData: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": `${BASE_URL}/ai-in-kuwait#webpage`,
          url: `${BASE_URL}/ai-in-kuwait`,
          name: "AI Agency in Kuwait | Use Cases & Enablement | Jamil Global",
          description:
            "Practical AI agency work in Kuwait: use cases, implementation paths, and enablement programs that ship into operations.",
          isPartOf: { "@id": `${BASE_URL}/#website` },
          about: { "@id": `${BASE_URL}/#organization` },
        },
        {
          "@type": "FAQPage",
          "@id": `${BASE_URL}/ai-in-kuwait#faq`,
          mainEntity: faqEntitiesFromList(aiInKuwaitPageFaqs),
        },
      ],
    },
  },
  dtaas: {
    title: "AI Development as a Service for Kuwait | SDaaS | Jamil Global",
    description:
      "SDaaS for Kuwait teams: discovery, build, and delivery for AI and software projects—embedded capacity with GCC-friendly engagement models.",
    keywords:
      "SDaaS Kuwait, AI development Kuwait, software as a service Kuwait, Kuwait AI projects, GCC development partner, dedicated team Kuwait",
    image: "/pictures/ai.webp",
    structuredData: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Service",
          "@id": `${BASE_URL}/dtaas#service`,
          name: "Development as a Service (SDaaS)",
          serviceType: "Software development as a service",
          description:
            "Embedded product-engineering capacity for discovery, build, and delivery—AI and classic software—with predictable rhythm for Kuwait and GCC teams.",
          provider: { "@id": `${BASE_URL}/#organization` },
          areaServed: [
            { "@type": "Country", name: "Kuwait" },
            { "@type": "Country", name: "Saudi Arabia" },
            { "@type": "Country", name: "United Arab Emirates" },
            { "@type": "Country", name: "Qatar" },
          ],
          url: `${BASE_URL}/dtaas`,
        },
        {
          "@type": "FAQPage",
          "@id": `${BASE_URL}/dtaas#faq`,
          mainEntity: faqEntitiesFromList(dtaasPageFaqs),
        },
      ],
    },
  },
  "case-studies": {
    title: "Kuwait AI Agency Case Studies | Projects & Results | Jamil Global",
    description:
      "Case studies from a Kuwait AI agency: automation, agents, data, and web work—outcomes you can measure, with GCC-relevant playbooks.",
    keywords:
      "Jamil Global case studies, Kuwait AI agency, Kuwait AI projects, AI automation case studies, web scraping case studies, GCC software portfolio",
    image: "/pictures/ai.webp",
    // Populated via getCaseStudiesListingStructuredData() so ItemList stays in sync.
  },
  blog: {
    title: "Kuwait AI Agency Insights | Enablement Blog | Jamil Global",
    description:
      "Notes from a Kuwait AI agency on enablement: integrations, automation, scraping, and modern delivery—aligned with GCC teams.",
    keywords:
      "Kuwait AI agency blog, AI enablement blog, automation Kuwait, GCC tech blog, Jamil Global insights, AI projects Kuwait",
    image: "/pictures/ai.webp",
  },
  "ad-budget-calculator": {
    title: "Ad Budget and Cost per Lead Calculator | Jamil Global",
    description:
      "Free ad budget calculator for Kuwait, UAE, Saudi Arabia, and Qatar. Work out clicks, leads, cost per lead, CAC, and ROAS, or the budget a lead target needs.",
    keywords:
      "ad budget calculator, cost per lead calculator, cpl calculator, cac calculator, roas calculator, cpc cpm calculator, ad budget kuwait, ad budget gulf, paid media calculator",
    structuredData: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebApplication",
          "@id": `${BASE_URL}/tools/ad-budget-calculator#app`,
          name: "Ad Budget and Cost per Lead Calculator",
          url: `${BASE_URL}/tools/ad-budget-calculator`,
          applicationCategory: "BusinessApplication",
          operatingSystem: "Any",
          description:
            "Free calculator for ad budget, clicks, leads, cost per lead, customer acquisition cost, and ROAS on CPC or CPM campaigns, with reverse mode for lead and cost-per-lead targets.",
          offers: {
            "@type": "Offer",
            price: "0",
            priceCurrency: "USD",
          },
          isAccessibleForFree: true,
          publisher: {
            "@type": "Organization",
            "@id": `${BASE_URL}/#organization`,
            name: BRAND_NAME,
            url: BASE_URL,
          },
        },
        {
          "@type": "FAQPage",
          "@id": `${BASE_URL}/tools/ad-budget-calculator#faq`,
          mainEntity: faqEntitiesFromList(adBudgetCalculatorFaqs),
        },
      ],
    },
  },
  card: {
    title: "Jamil Global — Connect | Kuwait AI Agency",
    description: `Quick links from Jamil Global: ${CONTACT_EMAIL}, Kuwait ${PHONE_KUWAIT.display}, WhatsApp ${PHONE_UAE.display}, website, and case studies.`,
    keywords:
      "Jamil Global contact, Kuwait AI agency contact, GCC software, hello@jamilglobal.com, Jamil Global phone, Jamil Global WhatsApp",
    structuredData: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": `${BASE_URL}/card#webpage`,
          name: "Jamil Global — Connect | Kuwait AI Agency",
          url: `${BASE_URL}/card`,
          description:
            "Contact highlights and links for Jamil Global — Kuwait AI agency for agents, automation, and product builds.",
          isPartOf: {
            "@type": "WebSite",
            "@id": `${BASE_URL}/#website`,
            name: BRAND_NAME,
            url: BASE_URL,
          },
          about: { "@id": `${BASE_URL}/#organization` },
        },
        {
          ...orgEntity(),
          "@id": `${BASE_URL}/card#org`,
        },
      ],
    },
  },
  "gulf-digital-marketing-statistics": {
    title: "Gulf Digital Marketing and AI Adoption Statistics 2026 | Jamil Global",
    description:
      "Sourced figures for Kuwait, UAE, Saudi Arabia and Qatar on internet use, social media, ad reach and generative AI adoption. Each links to its source.",
    keywords:
      "Gulf digital marketing statistics, Kuwait internet users, Kuwait social media statistics, UAE generative AI adoption, Saudi Arabia digital statistics, Qatar social media users, DataReportal Digital 2026, GCC AI adoption",
    image: "/pictures/ai.webp",
    structuredData: (() => {
      const pageUrl = `${BASE_URL}${GULF_STATS_PATH}`;
      const datasetId = `${pageUrl}#dataset`;
      const sourceRefs = Object.values(SOURCES).map((source) => ({
        "@type": "CreativeWork",
        name: `${source.publisher}, "${source.title}"`,
        url: source.url,
      }));
      const variables = [
        "Internet users and penetration of population",
        "Cellular connections as a share of population (GSMA Intelligence via DataReportal)",
        "Median mobile download speed in Mbps (Ookla via DataReportal)",
        "Social media user identities and their growth",
        "Platform ad reach and share of population (as reported by DataReportal)",
        "LinkedIn members",
        "Share of people aged 15 to 64 who used a generative AI product (Microsoft AI Economy Institute)",
      ];
      return {
        "@context": "https://schema.org",
        "@graph": [
          orgEntity(),
          personEntity(),
          {
            "@type": "Dataset",
            "@id": datasetId,
            name: "Gulf Digital Marketing and AI Adoption Statistics (Kuwait, UAE, KSA, Qatar)",
            description:
              "Sourced digital marketing and AI adoption figures for Kuwait, the UAE, Saudi Arabia and Qatar, compiled from DataReportal Digital 2026 country reports and Microsoft AI Economy Institute Q2 2026 data. Each figure links to its original source.",
            url: pageUrl,
            identifier: pageUrl,
            creator: { "@id": `${BASE_URL}/#organization` },
            publisher: { "@id": `${BASE_URL}/#organization` },
            datePublished: GULF_STATS_UPDATED,
            dateModified: GULF_STATS_UPDATED,
            version: "1.0",
            license: GULF_STATS_LICENSE,
            isAccessibleForFree: true,
            keywords: [
              "Kuwait",
              "UAE",
              "Saudi Arabia",
              "Qatar",
              "digital marketing",
              "internet users",
              "social media",
              "generative AI adoption",
            ],
            spatialCoverage: ["Kuwait", "United Arab Emirates", "Saudi Arabia", "Qatar"].map(
              (name) => ({ "@type": "Place", name })
            ),
            temporalCoverage: GULF_STATS_COVERAGE,
            variableMeasured: variables.map((name) => ({
              "@type": "PropertyValue",
              name,
            })),
            citation: sourceRefs,
            isBasedOn: sourceRefs,
            distribution: [
              {
                "@type": "DataDownload",
                encodingFormat: "text/csv",
                name: "Gulf digital marketing statistics (CSV)",
                contentUrl: `${BASE_URL}${GULF_STATS_CSV_PATH}`,
              },
            ],
          },
          {
            "@type": "Article",
            "@id": `${pageUrl}#article`,
            headline: "Gulf Digital Marketing and AI Adoption Statistics 2026",
            description:
              "A sourced reference for marketers and operators in Kuwait, the UAE, Saudi Arabia and Qatar.",
            url: pageUrl,
            mainEntityOfPage: pageUrl,
            mainEntity: { "@id": datasetId },
            author: authorPersonRef(),
            publisher: { "@id": `${BASE_URL}/#organization` },
            datePublished: GULF_STATS_UPDATED,
            dateModified: GULF_STATS_UPDATED,
          },
          {
            "@type": "FAQPage",
            "@id": `${pageUrl}#faq`,
            mainEntity: faqEntitiesFromList(gulfStatsFaqs),
          },
          {
            "@type": "BreadcrumbList",
            "@id": `${pageUrl}#breadcrumb`,
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: BASE_URL },
              {
                "@type": "ListItem",
                position: 2,
                name: "Gulf Digital Marketing and AI Adoption Statistics",
                item: pageUrl,
              },
            ],
          },
        ],
      };
    })(),
  },
};

/** Prefer the shared Organization entity so sameAs / knowsAbout stay consistent. */
const providerOrgRef = () => orgEntity();

// Helper function to get SEO data for a service
export const getServiceSEO = (serviceSlug) => {
  const serviceData = serviceSEOData[serviceSlug] || serviceSEOData["ai-integration"];
  const structuredData = serviceData.structuredData
    ? {
        ...serviceData.structuredData,
        provider: providerOrgRef(),
        areaServed: serviceData.structuredData.areaServed || [
          { "@type": "Country", name: "Kuwait" },
          { "@type": "Country", name: "Saudi Arabia" },
          { "@type": "Country", name: "United Arab Emirates" },
          { "@type": "Country", name: "Qatar" },
        ],
      }
    : undefined;

  return {
    ...serviceData,
    title: normalizeTitleWithBrand(serviceData.title),
    image: serviceData.image || "/pictures/ai.webp",
    ...(structuredData ? { structuredData } : {}),
  };
};

/** FAQs for a service slug (per-slug override or shared defaults). */
export const getServiceFaqs = (serviceSlug) =>
  serviceSlugFaqs[serviceSlug] || servicePagesFaqs;

/**
 * Full Service + FAQPage @graph for /services/:slug (runtime SEO + prerender).
 */
export const getServicePageStructuredData = (serviceSlug, baseUrl = BASE_URL) => {
  const seo = getServiceSEO(serviceSlug);
  const raw = seo.structuredData;
  if (!raw) return null;
  const { "@context": _ctx, ...serviceRest } = raw;
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        ...serviceRest,
        "@id": `${baseUrl}/services/${serviceSlug}#service`,
      },
      {
        "@type": "FAQPage",
        "@id": `${baseUrl}/services/${serviceSlug}#faq`,
        mainEntity: faqEntitiesFromList(getServiceFaqs(serviceSlug)),
      },
      ...(seo.breadcrumbName
        ? [
            {
              "@type": "BreadcrumbList",
              "@id": `${baseUrl}/services/${serviceSlug}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: baseUrl },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "Services",
                  item: `${baseUrl}/what-we-do`,
                },
                {
                  "@type": "ListItem",
                  position: 3,
                  name: seo.breadcrumbName,
                  item: `${baseUrl}/services/${serviceSlug}`,
                },
              ],
            },
          ]
        : []),
    ],
  };
};

function productFeatureList(slug) {
  const config = getProductDetailConfig(slug);
  const titles = (config?.capabilities || [])
    .map((c) => c.title)
    .filter(Boolean);
  return titles.length ? titles : undefined;
}

function productSoftwareApplication(slug, base) {
  const features = productFeatureList(slug);
  const { offerDescription, ...appFields } = base;
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    ...appFields,
    operatingSystem: appFields.operatingSystem || "Web",
    ...(features ? { featureList: features } : {}),
    offers: {
      "@type": "Offer",
      availability: "https://schema.org/InStock",
      priceCurrency: "KWD",
      description:
        offerDescription ||
        "Custom scoping and deployment — contact Jamil Global for a quote.",
      url: appFields.url,
      seller: { "@id": `${BASE_URL}/#organization` },
    },
    provider: providerOrgRef(),
    dateModified: SITE_CONTENT_UPDATED,
  };
}

/** SEO for /products/:slug */
export const productSEOData = {
  manzil: {
    title: "Manzil — Kuwait delivery operations platform | Jamil Global",
    description:
      "Manzil by Jamil Global: multi-sided Kuwait delivery OS—admin command center, merchant portal, driver app, and fleet company portal with live PostGIS dispatch, KWD wallets, and EN/AR RTL.",
    keywords:
      "Manzil, Kuwait delivery platform, delivery dispatch Kuwait, restaurant delivery software Kuwait, fleet portal, PostGIS live map, Jamil Global products",
    image: "/pictures/case-studies/manzil/02-admin-live-map.webp",
    structuredData: productSoftwareApplication("manzil", {
      name: "Manzil",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web, iOS, Android",
      url: "https://www.jamilglobal.com/products/manzil",
      description:
        "Multi-sided delivery operations platform for Kuwait restaurants and fleets—admin, merchant, driver, and company portals on one realtime stack.",
      offerDescription:
        "Custom deployment for Kuwait restaurants and fleets — contact for scoping and quote.",
    }),
  },
  ilmora: {
    title: "Ilmora — AI learning infrastructure for schools & universities | Jamil Global",
    description:
      "Ilmora by Jamil Global: bespoke, white-label AI learning platforms for K–12 and higher education—AI tutor, teacher copilot, assessments, multi-role workflows, secure handover, and full institutional ownership.",
    keywords:
      "Ilmora, Jamil Global Ilmora, school AI platform, AI tutor institution, teacher copilot GCC, white label education platform Kuwait, bespoke EdTech",
    image: "/pictures/ai.webp",
    structuredData: productSoftwareApplication("ilmora", {
      name: "Ilmora",
      applicationCategory: "EducationalApplication",
      operatingSystem: "Web",
      url: "https://ilmora.jamilglobal.com/",
      description:
        "Bespoke AI learning infrastructure for schools, colleges, and universities—designed and deployed by Jamil Global with white-label branding and operational handover.",
      offerDescription:
        "Bespoke institutional deployment with white-label branding and handover — quote on request.",
    }),
  },
  "mehfil-media": {
    title: "Mehfil Media — AI UGC for Pakistani brands | Jamil Global",
    description:
      "Mehfil Media by Jamil Global: AI UGC SaaS for Pakistani brands—upload a product photo, cast an AI creator, and generate Reel campaigns with desi hooks. Live at mehfil-media.jamilglobal.com.",
    keywords:
      "Mehfil Media, AI UGC Pakistan, AI creators Pakistan, product video SaaS, desi Reels, Jamil Global products, mehfil-media.jamilglobal.com",
    image: "/pictures/ai.webp",
    structuredData: productSoftwareApplication("mehfil-media", {
      name: "Mehfil Media",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      url: "https://mehfil-media.jamilglobal.com/",
      description:
        "AI UGC SaaS for Pakistani brands: upload product images, cast AI creators, and generate campaign Reels.",
      offerDescription:
        "AI UGC campaign packs for Pakistani brands — contact for pricing and pilots.",
    }),
  },
};

export const getProductSEO = (productSlug) => {
  const data = productSEOData[productSlug] || productSEOData.ilmora;
  return {
    ...data,
    title: normalizeTitleWithBrand(data.title),
  };
};

// Helper function to get SEO data for a page
export const getPageSEO = (pageSlug) => {
  const pageData = pageSEOData[pageSlug] || pageSEOData.home;
  const withListing =
    pageSlug === "case-studies"
      ? {
          ...pageData,
          structuredData: getCaseStudiesListingStructuredData(),
        }
      : pageData;
  return {
    ...withListing,
    title: normalizeTitleWithBrand(withListing.title),
  };
};

/** Flat portfolio list for CollectionPage ItemList. */
export const getAllCaseStudyProjects = () =>
  Object.values(projectCategories).flat();

/** /case-studies hub — CollectionPage + ItemList */
export const getCaseStudiesListingStructuredData = (baseUrl = BASE_URL) => {
  const projects = getAllCaseStudyProjects();
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CollectionPage",
        "@id": `${baseUrl}/case-studies#webpage`,
        url: `${baseUrl}/case-studies`,
        name: "Kuwait AI Agency Case Studies | Projects & Results | Jamil Global",
        description:
          "Case studies from a Kuwait AI agency: automation, agents, data, and web work—outcomes you can measure.",
        isPartOf: { "@id": `${baseUrl}/#website` },
        about: { "@id": `${baseUrl}/#organization` },
        dateModified: SITE_CONTENT_UPDATED,
        mainEntity: { "@id": `${baseUrl}/case-studies#itemlist` },
      },
      {
        "@type": "ItemList",
        "@id": `${baseUrl}/case-studies#itemlist`,
        numberOfItems: projects.length,
        itemListElement: projects.map((project, index) => ({
          "@type": "ListItem",
          position: index + 1,
          url: `${baseUrl}${project.path}`,
          name: project.title,
          description: project.description,
          item: {
            "@type": "Article",
            "@id": `${baseUrl}${project.path}#article`,
            headline: project.title,
            description: project.description,
            url: `${baseUrl}${project.path}`,
          },
        })),
      },
    ],
  };
};

/**
 * Individual case study — Article (extractable) with case-study sectioning.
 * schema.org has no CaseStudy type; Article + articleSection is the durable pattern.
 */
export const generateCaseStudyArticleSchema = (
  project,
  routePath,
  baseUrl = BASE_URL
) => {
  const title = project?.title || "Case Study";
  const description = project?.description || "";
  const imagePath = project?.ogImage || project?.image || null;
  const absoluteImage = imagePath
    ? /^https?:\/\//i.test(imagePath)
      ? imagePath
      : `${baseUrl}${imagePath.startsWith("/") ? imagePath : `/${imagePath}`}`
    : `${baseUrl}/og-image.png`;

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        "@id": `${baseUrl}${routePath}#article`,
        headline: title,
        name: title,
        description,
        url: `${baseUrl}${routePath}`,
        image: absoluteImage,
        inLanguage: "en",
        articleSection: "Case Study",
        about: project?.category || "Case Study",
        keywords: Array.isArray(project?.technologies)
          ? project.technologies.join(", ")
          : undefined,
        dateModified: SITE_CONTENT_UPDATED,
        author: authorPersonRef(baseUrl),
        publisher: {
          "@type": "Organization",
          "@id": `${baseUrl}/#organization`,
          name: BRAND_NAME,
          logo: {
            "@type": "ImageObject",
            url: `${baseUrl}/logo.png`,
          },
        },
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": `${baseUrl}${routePath}`,
        },
        isPartOf: {
          "@type": "CollectionPage",
          "@id": `${baseUrl}/case-studies#webpage`,
        },
      },
      personSchema(baseUrl),
      generateBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Case Studies", url: "/case-studies" },
        { name: title, url: routePath },
      ]),
    ],
  };
};

// Generate breadcrumb structured data
export const generateBreadcrumbSchema = (items) => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url ? `${BASE_URL}${item.url}` : undefined,
    })),
  };
};

// Generate FAQ schema
export const generateFAQSchema = (faqs) => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
};

// Generate BlogPosting schema for an individual article
export const generateBlogPostingSchema = (post, slug) => {
  if (!post) return null;

  const publishedDate = post.publishedAt || post.createdAt;
  const modifiedDate = post.updatedAt || publishedDate;
  const baseUrl = getPublicBaseUrl();
  const canonical = `${baseUrl}/blog/${slug}`;
  const rawImage = post.image || post.featuredImage || post.coverImage;
  const articleImage = resolveBlogImage(rawImage, slug);
  const absoluteImage = (() => {
    if (!articleImage) return `${baseUrl}/og-image.png`;
    const s = String(articleImage);
    if (/^https?:\/\//i.test(s)) return s;
    return `${baseUrl}${s.startsWith("/") ? s : `/${s}`}`;
  })();

  const faqPairs = extractFaqPairs(post.content || post.body || "");
  const faqNode =
    faqPairs.length > 0
      ? {
          "@type": "FAQPage",
          "@id": `${canonical}#faq`,
          mainEntity: faqEntitiesFromPairs(faqPairs),
        }
      : null;

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BlogPosting",
        "@id": `${canonical}#article`,
        headline: post.title || "Untitled",
        url: canonical,
        description: post.description || "",
        image: absoluteImage,
        datePublished: publishedDate || undefined,
        dateModified: modifiedDate || undefined,
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": canonical,
        },
        author: authorPersonRef(baseUrl),
        publisher: {
          "@type": "Organization",
          "@id": `${baseUrl}/#organization`,
          name: BRAND_NAME,
          logo: {
            "@type": "ImageObject",
            url: `${baseUrl}/logo.png`,
          },
        },
        inLanguage: "en",
        isPartOf: {
          "@type": "Blog",
          "@id": `${baseUrl}/blog#blog`,
          name: "Jamil Global Blog",
          url: `${baseUrl}/blog`,
        },
        keywords: Array.isArray(post.tags)
          ? post.tags
              .map((tag) => (typeof tag === "string" ? tag : tag?.name))
              .filter(Boolean)
              .join(", ")
          : undefined,
      },
      personSchema(baseUrl),
      generateBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Blog", url: "/blog" },
        { name: post.title || "Untitled", url: `/blog/${slug}` },
      ]),
      ...(faqNode ? [faqNode] : []),
    ],
  };
};

// Generate structured data for the blog listing page — Blog + ItemList
export const generateBlogListingSchema = (payload) => {
  const baseUrl = getPublicBaseUrl();
  const posts = Array.isArray(payload?.posts) ? payload.posts : [];
  const listItems = posts.slice(0, 20).map((post, index) => ({
    "@type": "ListItem",
    position: index + 1,
    url: `${baseUrl}/blog/${post.slug}`,
    item: {
      "@type": "BlogPosting",
      headline: post.title || "Untitled",
      description: post.description || "",
      url: `${baseUrl}/blog/${post.slug}`,
      datePublished: post.publishedAt || post.createdAt || undefined,
      dateModified:
        post.updatedAt || post.publishedAt || post.createdAt || undefined,
    },
  }));

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Blog",
        "@id": `${baseUrl}/blog#blog`,
        url: `${baseUrl}/blog`,
        name: payload?.title || "Jamil Global Blog",
        description: payload?.description || "",
        publisher: { "@id": `${baseUrl}/#organization` },
        inLanguage: "en",
        blogPost: listItems.map((li) => li.item),
      },
      {
        "@type": "ItemList",
        "@id": `${baseUrl}/blog#itemlist`,
        numberOfItems: listItems.length,
        itemListElement: listItems,
      },
    ],
  };
};
